import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Mic, Square, Play, Pause, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import {
  uploadFileToBlob,
} from '../../api/services/storageService';
import { ThemedText, ConfirmDialog } from '../../components';

const WAVEFORM_BAR_COUNT = 40;
const METER_TICK_MS = 80;

// ==================== Styled ====================

const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 1;   }
  50%  { transform: scale(1.3);  opacity: 0.55;}
  100% { transform: scale(1);    opacity: 1;   }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
`;

const Tile = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.backgroundSecondary || p.theme.colors.background};
  min-height: 60px;
`;

const RoundButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 21px;
  border: none;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  background: ${p => p.$variant === 'primary' ? p.theme.colors.tint : p.theme.colors.danger};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  transition: transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.22);
  }
`;

const SecondaryButton = styled.button`
  padding: 6px 12px;
  border-radius: ${p => p.theme.borderRadius.sm};
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: ${p => p.theme.colors.tint};
    color: ${p => p.theme.colors.tint};
    background: ${p => p.theme.colors.tint + '08'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(SecondaryButton)`
  color: ${p => p.theme.colors.danger};
  border-color: ${p => p.theme.colors.danger}55;

  &:hover:not(:disabled) {
    border-color: ${p => p.theme.colors.danger};
    color: ${p => p.theme.colors.danger};
    background: ${p => p.theme.colors.danger}10;
  }
`;

const PulseDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 5px;
  background: ${p => p.theme.colors.danger};
  animation: ${pulse} 1s infinite;
  display: inline-block;
  flex-shrink: 0;
`;

const WaveformBars = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  height: 36px;
  min-width: 0;
`;

const Bar = styled.div`
  flex: 1;
  min-width: 2px;
  border-radius: 1px;
  height: ${p => Math.max(3, (p.$amp || 0) * 36)}px;
  background: ${p => (p.$active
    ? p.theme.colors.tint
    : p.theme.colors.muted + '80')};
  transition: background 0.1s;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
  ${p => (p.$grow ? 'flex: 1;' : '')}
`;

const Caption = styled.span`
  color: ${p => p.theme.colors.muted};
  font-size: 11px;
`;

const DurationText = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: ${p => p.theme.colors.text};
`;

const ProgressBar = styled.div`
  height: 3px;
  background: ${p => p.theme.colors.border};
  border-radius: 2px;
  overflow: hidden;
  margin-top: 4px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${p => p.theme.colors.tint};
  width: ${p => p.$pct}%;
  transition: width 0.15s;
`;

const ErrorCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.danger}80;
  background: ${p => p.theme.colors.danger}12;
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.danger};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// ==================== Helpers ====================

const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/mpeg',
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
};

const extFromMime = (mime) => {
  if (!mime) return 'webm';
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mpeg')) return 'mp3';
  return 'webm';
};

const bitrateFromQuality = (quality) => {
  switch (quality) {
    case 'low':  return 24000;
    case 'high': return 128000;
    case 'medium':
    default:     return 64000;
  }
};

const formatDuration = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const computeEnvelope = (audioBuffer, bars = WAVEFORM_BAR_COUNT) => {
  const channel = audioBuffer.getChannelData(0);
  const blockSize = Math.max(1, Math.floor(channel.length / bars));
  const out = new Array(bars).fill(0);
  for (let i = 0; i < bars; i++) {
    let sum = 0;
    const start = i * blockSize;
    const end = Math.min(channel.length, start + blockSize);
    for (let j = start; j < end; j++) sum += Math.abs(channel[j]);
    const count = Math.max(1, end - start);
    out[i] = Math.min(1, (sum / count) * 2.5);
  }
  return out;
};

// ==================== Component ====================

/**
 * VoiceFieldRenderer — in-browser voice recording with Telegram-style waveform.
 * Uploads to Azure Blob via SAS and writes the temp blob path into the form value.
 *
 * @param {Object}   field     Schema field
 * @param {string}   value     Current form value (temp blob path or null)
 * @param {Function} onChange  Called with (blobPath | null)
 * @param {boolean}  disabled  Disable all interaction
 */
const VoiceFieldRenderer = ({ field, value, onChange, disabled, attachment }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { props = {} } = field;

  const maxDuration = Math.max(5, Number(props.maxDurationSeconds) || 300);
  const enableTranscription = props.enableTranscription !== false;
  const language = props.transcriptionLanguage || 'en-US';

  // State machine: idle | unsupported | denied | recording | uploading | ready | error
  const [state, setState] = useState(() => {
    if (typeof navigator === 'undefined' ||
        !navigator.mediaDevices?.getUserMedia ||
        typeof MediaRecorder === 'undefined') {
      return 'unsupported';
    }
    return value ? 'ready' : 'idle';
  });

  const [liveBars, setLiveBars]     = useState(() => new Array(WAVEFORM_BAR_COUNT).fill(0));
  const [staticBars, setStaticBars] = useState([]);
  const [elapsedMs, setElapsedMs]   = useState(0);
  const [playing, setPlaying]       = useState(false);
  const [playheadBar, setPlayheadBar] = useState(-1);
  const [uploadPct, setUploadPct]   = useState(0);
  const [errorMsg, setErrorMsg]     = useState('');
  const [objectUrl, setObjectUrl]   = useState(null);
  const [confirmIntent, setConfirmIntent] = useState(null); // 'delete' | 'rerecord' | null

  const mediaRecorderRef = useRef(null);
  const streamRef        = useRef(null);
  const audioContextRef  = useRef(null);
  const analyserRef      = useRef(null);
  const rafRef           = useRef(null);
  const tickTimerRef     = useRef(null);
  const recordStartRef   = useRef(0);
  const chunksRef        = useRef([]);
  const mimeRef          = useRef('');
  const recordedBlobRef  = useRef(null);
  const audioElRef       = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStreams();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStreams = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    tickTimerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
    analyserRef.current = null;
  };

  // ---- Recording ----

  const startRecording = async () => {
    setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Live amplitude analyser
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      const src = ctx.createMediaStreamSource(stream);
      src.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;

      // MediaRecorder
      const mime = pickMime();
      mimeRef.current = mime;
      const mrOpts = mime
        ? { mimeType: mime, audioBitsPerSecond: bitrateFromQuality(props.quality) }
        : undefined;
      const mr = new MediaRecorder(stream, mrOpts);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = handleRecordingStopped;
      mr.start(200);
      recordStartRef.current = performance.now();
      setElapsedMs(0);
      setLiveBars(new Array(WAVEFORM_BAR_COUNT).fill(0));
      setState('recording');

      // Waveform sampling via rAF + throttle
      let lastSample = 0;
      const dataBuf = new Uint8Array(analyser.fftSize);
      const tick = (t) => {
        if (!analyserRef.current) return;
        rafRef.current = requestAnimationFrame(tick);
        if (t - lastSample < METER_TICK_MS) return;
        lastSample = t;
        analyser.getByteTimeDomainData(dataBuf);
        let sumSq = 0;
        for (let i = 0; i < dataBuf.length; i++) {
          const v = (dataBuf[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / dataBuf.length);
        const amp = Math.min(1, rms * 3);
        setLiveBars(prev => {
          const next = prev.slice(1);
          next.push(amp);
          return next;
        });
      };
      rafRef.current = requestAnimationFrame(tick);

      // Duration counter + auto-stop
      tickTimerRef.current = setInterval(() => {
        const elapsed = performance.now() - recordStartRef.current;
        setElapsedMs(elapsed);
        if (elapsed >= maxDuration * 1000) {
          stopRecording();
        }
      }, 100);
    } catch (err) {
      console.error('Microphone error:', err);
      if (err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
        setState('denied');
      } else {
        setErrorMsg(err?.message || 'Could not start recording.');
        setState('error');
      }
      stopStreams();
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      try { mr.stop(); } catch (_) { /* no-op */ }
    }
  };

  const handleRecordingStopped = async () => {
    const mime = mimeRef.current || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type: mime });
    chunksRef.current = [];
    recordedBlobRef.current = blob;
    stopStreams();

    const localUrl = URL.createObjectURL(blob);
    setObjectUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return localUrl;
    });

    // Build static envelope for playback bars
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const decodeCtx = new AudioCtx();
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer.slice(0));
      setStaticBars(computeEnvelope(audioBuffer));
      decodeCtx.close().catch(() => {});
    } catch (_) {
      // Safari sometimes can't decode webm opus even if it recorded mp4;
      // fall back to the last live waveform so the bar still looks alive.
      setStaticBars(prev => (prev.length ? prev : liveBars.slice()));
    }

    await uploadBlob(blob);
  };

  // ---- Upload ----

  const uploadBlob = async (blob) => {
    setState('uploading');
    setUploadPct(0);
    try {
      const mime = mimeRef.current || blob.type || 'audio/webm';
      const ext = extFromMime(mime);
      const filename = `voice_${field.id}_${Date.now()}.${ext}`;
      const file = new File([blob], filename, { type: mime });

      const { blobPath } = await uploadFileToBlob(
        file,
        (_, __, pct) => setUploadPct(pct),
      );

      onChange(blobPath);
      setUploadPct(100);
      setState('ready');
    } catch (err) {
      console.error('Voice upload failed:', err);
      setErrorMsg('Upload failed — check your connection and retry.');
      setState('error');
    }
  };

  const handleRetryUpload = () => {
    if (recordedBlobRef.current) uploadBlob(recordedBlobRef.current);
  };

  // ---- Reset / re-record ----

  const resetToIdle = () => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    recordedBlobRef.current = null;
    setStaticBars([]);
    setPlaying(false);
    setPlayheadBar(-1);
    setElapsedMs(0);
    setUploadPct(0);
    setErrorMsg('');
    onChange(null);
    setState('idle');
  };

  const handleConfirm = () => {
    const intent = confirmIntent;
    setConfirmIntent(null);
    resetToIdle();
    if (intent === 'rerecord') {
      // Defer to next tick so state flushes before mic request
      setTimeout(() => { startRecording(); }, 0);
    }
  };

  // ---- Playback ----

  const handlePlayToggle = () => {
    const el = audioElRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const el = audioElRef.current;
    if (!el || !el.duration || !isFinite(el.duration)) return;
    const bar = Math.min(
      WAVEFORM_BAR_COUNT - 1,
      Math.floor((el.currentTime / el.duration) * WAVEFORM_BAR_COUNT),
    );
    setPlayheadBar(bar);
  };

  const handleEnded = () => {
    setPlaying(false);
    setPlayheadBar(-1);
  };

  // ==================== Render ====================

  if (state === 'unsupported') {
    return (
      <ErrorCard theme={theme}>
        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>Voice recording not supported</div>
          <ThemedText type="small" style={{ color: theme.colors.danger }}>
            This browser doesn't allow in-page audio recording. Open the form in the aQRate mobile app or a recent Chrome/Firefox/Edge build.
          </ThemedText>
        </div>
      </ErrorCard>
    );
  }

  if (state === 'denied') {
    return (
      <Container theme={theme}>
        <Tile theme={theme}>
          <AlertTriangle size={20} color={theme.colors.danger} style={{ flexShrink: 0 }} />
          <TextColumn $grow>
            <ThemedText type="small">Microphone access is blocked.</ThemedText>
            <Caption theme={theme}>
              Allow microphone access in your browser's site settings, then try again.
            </Caption>
          </TextColumn>
          <SecondaryButton theme={theme} type="button" onClick={startRecording} disabled={disabled}>
            <RotateCcw size={12} /> Retry
          </SecondaryButton>
        </Tile>
      </Container>
    );
  }

  if (state === 'recording') {
    return (
      <Container theme={theme}>
        <Tile theme={theme}>
          <RoundButton theme={theme} type="button" onClick={stopRecording} title="Stop recording">
            <Square size={16} fill="#fff" />
          </RoundButton>
          <WaveformBars>
            {liveBars.map((amp, i) => (
              <Bar key={i} theme={theme} $amp={amp} />
            ))}
          </WaveformBars>
          <TextColumn>
            <Row style={{ gap: 6 }}>
              <PulseDot theme={theme} />
              <DurationText theme={theme}>
                {formatDuration(elapsedMs / 1000)} / {formatDuration(maxDuration)}
              </DurationText>
            </Row>
            <Caption theme={theme}>Recording…</Caption>
          </TextColumn>
        </Tile>
      </Container>
    );
  }

  if (state === 'uploading') {
    return (
      <Container theme={theme}>
        <Tile theme={theme}>
          <RoundButton theme={theme} type="button" $variant="primary" disabled>
            <Mic size={18} />
          </RoundButton>
          <TextColumn $grow>
            <ThemedText type="small">Uploading recording…</ThemedText>
            <ProgressBar theme={theme}>
              <ProgressFill theme={theme} $pct={uploadPct} />
            </ProgressBar>
          </TextColumn>
          <DurationText theme={theme}>{uploadPct}%</DurationText>
        </Tile>
      </Container>
    );
  }

  if (state === 'ready') {
    const bars = staticBars.length ? staticBars : new Array(WAVEFORM_BAR_COUNT).fill(0.45);
    return (
      <Container theme={theme}>
        <Tile theme={theme}>
          <RoundButton
            theme={theme}
            type="button"
            $variant="primary"
            onClick={handlePlayToggle}
            disabled={!objectUrl}
            title={!objectUrl ? 'Re-record to preview' : (playing ? 'Pause' : 'Play')}
          >
            {playing ? <Pause size={18} /> : <Play size={18} />}
          </RoundButton>
          <WaveformBars>
            {bars.map((amp, i) => (
              <Bar
                key={i}
                theme={theme}
                $amp={amp}
                $active={i <= playheadBar}
              />
            ))}
          </WaveformBars>
          <TextColumn>
            <Caption theme={theme} style={{ color: theme.colors.tint, fontWeight: 600 }}>
              Recorded
            </Caption>
            {enableTranscription && (
              <Caption theme={theme}>Transcribe ({language === 'auto' ? 'auto-detect' : language})</Caption>
            )}
          </TextColumn>
        </Tile>
        <Row>
          <SecondaryButton
            theme={theme}
            type="button"
            onClick={() => setConfirmIntent('rerecord')}
            disabled={disabled}
          >
            <RotateCcw size={12} /> Re-record
          </SecondaryButton>
          <DangerButton
            theme={theme}
            type="button"
            onClick={() => setConfirmIntent('delete')}
            disabled={disabled}
          >
            <Trash2 size={12} /> Delete
          </DangerButton>
        </Row>
        {attachment && enableTranscription && (() => {
          const st = attachment.transcriptStatus;
          if (!st || st === 'NotRequested') return null;
          if (st === 'Completed' && attachment.transcript) {
            return (
              <div style={{
                padding: theme.spacing.sm,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius?.md || 8,
                background: theme.colors.backgroundSecondary || theme.colors.background,
              }}>
                <div style={{ color: theme.colors.muted, fontSize: '12px', fontWeight: 600, marginBottom: 2 }}>
                  Transcript
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', marginLeft: 8,
                    borderRadius: 10, fontSize: '11px', fontWeight: 600,
                    color: '#fff', background: theme.colors.success || '#2e7d32',
                  }}>Completed</span>
                  {attachment.transcriptLanguage && (
                    <span style={{ marginLeft: 6, color: theme.colors.muted, fontWeight: 400 }}>
                      ({attachment.transcriptLanguage})
                    </span>
                  )}
                </div>
                <div style={{ color: theme.colors.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                  {attachment.transcript}
                </div>
              </div>
            );
          }
          if (st === 'Pending' || st === 'InProgress') {
            return (
              <div style={{ color: theme.colors.muted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  display: 'inline-block', padding: '2px 8px',
                  borderRadius: 10, fontSize: '11px', fontWeight: 600,
                  color: '#fff', background: theme.colors.tint,
                }}>Transcribing…</span>
              </div>
            );
          }
          if (st === 'Failed') {
            return (
              <div style={{ color: theme.colors.danger || '#c62828', fontSize: '12px' }}>
                Transcription failed
                <span style={{
                  display: 'inline-block', padding: '2px 8px', marginLeft: 8,
                  borderRadius: 10, fontSize: '11px', fontWeight: 600,
                  color: '#fff', background: theme.colors.danger || '#c62828',
                }}>Failed</span>
              </div>
            );
          }
          return null;
        })()}
        {objectUrl && (
          <audio
            ref={audioElRef}
            src={objectUrl}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            preload="metadata"
            style={{ display: 'none' }}
          />
        )}
        <ConfirmDialog
          isOpen={confirmIntent !== null}
          title={confirmIntent === 'rerecord' ? 'Re-record voice' : 'Remove recording'}
          message={confirmIntent === 'rerecord'
            ? 'Discard this recording and start a new one?'
            : 'Discard this recording? This cannot be undone.'}
          confirmLabel={confirmIntent === 'rerecord' ? 'Re-record' : 'Delete'}
          cancelLabel="Cancel"
          onConfirm={handleConfirm}
          onCancel={() => setConfirmIntent(null)}
        />
      </Container>
    );
  }

  if (state === 'error') {
    return (
      <Container theme={theme}>
        <ErrorCard theme={theme}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Recording issue</div>
            <ThemedText type="small" style={{ color: theme.colors.danger }}>
              {errorMsg || 'Something went wrong while recording.'}
            </ThemedText>
          </div>
        </ErrorCard>
        <Row>
          {recordedBlobRef.current && (
            <SecondaryButton theme={theme} type="button" onClick={handleRetryUpload}>
              <RotateCcw size={12} /> Retry upload
            </SecondaryButton>
          )}
          <SecondaryButton
            theme={theme}
            type="button"
            onClick={() => { resetToIdle(); }}
          >
            Start over
          </SecondaryButton>
        </Row>
      </Container>
    );
  }

  // idle
  return (
    <Container theme={theme}>
      <Tile theme={theme}>
        <RoundButton
          theme={theme}
          type="button"
          onClick={startRecording}
          disabled={disabled}
          title="Tap to record"
        >
          <Mic size={18} />
        </RoundButton>
        <TextColumn $grow>
          <ThemedText type="small">{props.placeholder || 'Tap to record'}</ThemedText>
          <Caption theme={theme}>
            Up to {formatDuration(maxDuration)}
            {enableTranscription ? ` · transcribe (${language === 'auto' ? 'auto-detect' : language})` : ''}
          </Caption>
        </TextColumn>
      </Tile>
    </Container>
  );
};

export default VoiceFieldRenderer;
export { VoiceFieldRenderer };
